import React, { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, LayoutGrid, LayoutTemplate } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDashboardById } from '@/hooks/useDashboards';
import { DashboardOverlayContent } from './DashboardOverlayContent';

interface WidgetsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  widgetsDashboardId?: string | null;
}

const SWIPE_THRESHOLD = 40;

export const WidgetsOverlay: React.FC<WidgetsOverlayProps> = ({
  isOpen,
  onClose,
  widgetsDashboardId,
}) => {
  const navigate = useNavigate();
  const { dashboard } = useDashboardById(widgetsDashboardId);
  const [dragOffset, setDragOffset] = useState(0);
  const startPos = useRef({ x: 0, y: 0, t: 0 });

  const panelRef = useRef<HTMLDivElement>(null);
  const didCaptureRef = useRef(false);

  const isInteractiveElement = (el: HTMLElement | null) => {
    if (!el) return false;
    const tag = el.tagName?.toLowerCase();
    const role = el.getAttribute?.('role');
    if (tag === 'button' || tag === 'a' || tag === 'input' || tag === 'select' || tag === 'textarea') return true;
    if (role === 'button' || role === 'link' || role === 'menuitem') return true;
    if (el.closest?.('button, a, input, select, textarea, [role=button], [role=link], [data-dashboard-widget]')) return true;
    return false;
  };

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      startPos.current = { x: e.clientX, y: e.clientY, t: Date.now() };
      const target = e.target as HTMLElement;
      const isCloseButton = target.closest('[data-close-button]');
      const isInteractive = isInteractiveElement(target);
      if (!isCloseButton && !isInteractive) {
        panelRef.current?.setPointerCapture(e.pointerId);
        didCaptureRef.current = true;
      } else {
        didCaptureRef.current = false;
      }
    },
    []
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (e.buttons !== 1) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    if (dx > 0 && dx > Math.abs(dy)) setDragOffset(Math.min(dx, 120));
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (didCaptureRef.current) {
        panelRef.current?.releasePointerCapture(e.pointerId);
        didCaptureRef.current = false;
      }
      const dx = e.clientX - startPos.current.x;
      const dy = e.clientY - startPos.current.y;
      const dt = Date.now() - startPos.current.t;
      const velocity = dt > 0 ? dx / dt : 0;
      const quickSwipe = velocity > 0.5 && dx > 20;
      if ((dx >= SWIPE_THRESHOLD && dx > Math.abs(dy)) || quickSwipe) onClose();
      setDragOffset(0);
    },
    [onClose]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      t: Date.now(),
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - startPos.current.x;
    const dy = e.touches[0].clientY - startPos.current.y;
    if (dx > 0 && dx > Math.abs(dy)) setDragOffset(Math.min(dx, 120));
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const end = e.changedTouches[0];
      const dx = end ? end.clientX - startPos.current.x : 0;
      const dy = end ? end.clientY - startPos.current.y : 0;
      const dt = Date.now() - startPos.current.t;
      const velocity = dt > 0 ? dx / dt : 0;
      const quickSwipe = velocity > 0.5 && dx > 20;
      if ((dx >= SWIPE_THRESHOLD && dx > Math.abs(dy)) || quickSwipe) onClose();
      setDragOffset(0);
    },
    [onClose]
  );

  const handleCloseClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    },
    [onClose]
  );

  if (!isOpen) return null;

  const content = (
    <div
      className="fixed inset-0 z-[100] flex justify-end pointer-events-none"
    >
      {/* Panel - full height, small black background 90% opacity; swipe right to dismiss */}
      <div
        ref={panelRef}
        className={cn(
          'pointer-events-auto w-[340px] min-w-[340px] h-full',
          'transition-transform duration-150 ease-out',
          'backdrop-blur-[2px]',
          'animate-in slide-in-from-right duration-300'
        )}
        style={{
          transform: `translateX(${dragOffset}px)`,
          background: 'linear-gradient(to left, rgba(0,0,0,0.1), transparent)',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerLeave={() => setDragOffset(0)}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => setDragOffset(0)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="flex flex-col h-full">
          {/* Header - compact, drag right to close */}
          <div className="flex items-center justify-between px-4 py-2">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <LayoutGrid className="h-5 w-5" />
              Widgets
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCloseClick}
              className="shrink-0 z-10"
              aria-label="Close"
              data-close-button
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Body - widgets moved up, pan-y allows vertical scroll */}
          <div className="flex flex-col flex-1 min-h-0" style={{ touchAction: 'pan-y' }}>
            <div className="flex-1 min-h-0 overflow-auto px-4 pt-2 pb-2">
              {widgetsDashboardId ? (
                <DashboardOverlayContent
                  dashboardId={widgetsDashboardId}
                  emptyMessage="No widgets yet. Add components in the Dashboard Builder."
                  className="mb-4"
                  compact={dashboard?.preview_compact ?? false}
                  bordered
                />
              ) : (
                <p className="text-sm text-muted-foreground mb-4">
                  No Widgets dashboard for this brand. You see your personal one first, then the brand&apos;s shared, then the global default. Create or choose one in the Dashboard Builder.
                </p>
              )}
            </div>

            <Button
              variant="ghost"
              className="w-full shrink-0 rounded-none py-3 text-muted-foreground hover:bg-transparent hover:text-foreground"
              onClick={() => navigate('/admin/dashboard-builder')}
            >
              <LayoutTemplate className="h-4 w-4 mr-2" />
              Edit Widgets
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
